import React, { useCallback, useEffect, useState } from 'react';

/**
 * ResizeHandle - Draggable handle for resizing panels
 *
 * @param {string} direction - 'left' or 'right' - which direction the resize affects
 * @param {function} onResize - Callback with new width
 * @param {number} minWidth - Minimum width constraint
 * @param {number} maxWidth - Maximum width constraint
 */
function ResizeHandle({ direction = 'right', onResize, minWidth = 200, maxWidth = 600 }) {
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    setStartX(e.clientX);
    // Get initial width from parent element
    const parent = e.target.parentElement;
    setStartWidth(parent?.offsetWidth || 280);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const diff = direction === 'right'
      ? e.clientX - startX
      : startX - e.clientX;

    const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + diff));
    onResize?.(newWidth);
  }, [isDragging, startX, startWidth, direction, minWidth, maxWidth, onResize]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div
      onMouseDown={handleMouseDown}
      className={`
        absolute top-0 ${direction === 'right' ? 'right-0' : 'left-0'}
        w-1 h-full cursor-col-resize
        hover:bg-decisiv-blue/50 active:bg-decisiv-blue
        transition-colors z-60
        ${isDragging ? 'bg-decisiv-blue' : 'bg-transparent hover:bg-gray-300 dark:hover:bg-gray-600'}
      `}
      title="Drag to resize"
    />
  );
}

export default ResizeHandle;
